import React, { useState, useEffect } from 'react';
import { microsoftService } from '../../../../../services/microsoftService';
import { WidgetLoadingState } from '../../../design';
import { getErrorStatus } from '../../../../../utils/errors';

export interface OutlookWidgetConfig {
  defaultTo?: string[];
  defaultSubject?: string;
  defaultBody?: string;
  bodyType?: 'HTML' | 'Text';
  allowEditRecipients?: boolean;
  allowEditSubject?: boolean;
  allowEditBody?: boolean;
  sendMode?: 'draft' | 'send' | 'compose';
  showComposeButton?: boolean;
}

interface OutlookWidgetRendererProps {
  config: OutlookWidgetConfig;
  onEmailSent?: (messageId?: string) => void;
  readOnly?: boolean;
}

const OutlookWidgetRenderer: React.FC<OutlookWidgetRendererProps> = ({
  config,
  onEmailSent,
  readOnly = false,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [to, setTo] = useState<string>(config.defaultTo?.join(', ') || '');
  const [subject, setSubject] = useState(config.defaultSubject || '');
  const [body, setBody] = useState(config.defaultBody || '');

  // Check Microsoft connection status
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const status = await microsoftService.getConnectionStatus();
        setIsConnected(status.connected);
      } catch (err) {
        // Not connected
      } finally {
        setIsLoading(false);
      }
    };
    checkConnection();
  }, []);

  const connectMicrosoft = async () => {
    try {
      const { authUrl } = await microsoftService.getAuthUrl();
      sessionStorage.setItem('microsoft_auth_return', window.location.href);
      window.location.href = authUrl;
    } catch (err) {
      setError('Failed to start Microsoft authentication');
    }
  };

  const handleSend = async () => {
    if (!to.trim()) {
      setError('Please enter at least one recipient');
      return;
    }

    setIsSending(true);
    setError(null);
    setSuccess(null);

    try {
      const recipients = to.split(',').map((email) => email.trim()).filter(Boolean);

      if (config.sendMode === 'draft') {
        const result = await microsoftService.createDraft({
          to: recipients,
          subject,
          body,
          bodyType: config.bodyType || 'HTML',
        });
        setSuccess('Draft created successfully!');
        onEmailSent?.(result.messageId);
      } else if (config.sendMode === 'send') {
        await microsoftService.sendEmail({
          to: recipients,
          subject,
          body,
          bodyType: config.bodyType || 'HTML',
        });
        setSuccess('Email sent successfully!');
        onEmailSent?.();
      }
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        setIsConnected(false);
        setError('Microsoft connection expired. Please reconnect.');
      } else {
        setError('Failed to send email');
      }
    } finally {
      setIsSending(false);
    }
  };

  const openInOutlook = async () => {
    const recipients = to.split(',').map((email) => email.trim()).filter(Boolean);
    await microsoftService.openOutlookCompose(
      recipients.join(';'),
      subject,
      body,
      true // prefer desktop
    );
  };


  const openMailto = () => {
    const recipients = to.split(',').map((email) => email.trim()).filter(Boolean);
    microsoftService.openMailto(recipients.join(','), subject, body);
  };

  // Compose mode - just show button to open email
  if (config.sendMode === 'compose' || config.showComposeButton) {
    return (
      <div className="h-full flex flex-col p-4">
        <div className="mb-4">
          <h3 className="font-medium text-neutral-900 mb-2">Quick Email</h3>
          <p className="text-sm text-neutral-600">
            Click below to compose an email with pre-filled content.
          </p>
        </div>

        {/* Preview */}
        <div className="bg-neutral-50 rounded-lg p-3 mb-4 flex-1 overflow-auto">
          {config.defaultTo && config.defaultTo.length > 0 && (
            <div className="mb-2">
              <span className="text-xs font-medium text-neutral-500">To:</span>
              <p className="text-sm text-neutral-900">{config.defaultTo.join(', ')}</p>
            </div>
          )}
          {config.defaultSubject && (
            <div className="mb-2">
              <span className="text-xs font-medium text-neutral-500">Subject:</span>
              <p className="text-sm text-neutral-900">{config.defaultSubject}</p>
            </div>
          )}
          {config.defaultBody && (
            <div>
              <span className="text-xs font-medium text-neutral-500">Body:</span>
              <p className="text-sm text-neutral-900 whitespace-pre-wrap">{config.defaultBody}</p>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={openInOutlook}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            Open in Outlook
          </button>
          <button
            onClick={openMailto}
            className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
            title="Open in default email app"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Not connected state - show connect button or allow mailto
  if (!isConnected && !isLoading) {
    return (
      <div className="h-full flex flex-col p-4">
        <div className="flex-1">
          {/* Email form */}
          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">To</label>
              <input
                type="text"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={config.allowEditRecipients === false || readOnly}
                placeholder="email@example.com, another@example.com"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none disabled:bg-neutral-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={config.allowEditSubject === false || readOnly}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none disabled:bg-neutral-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Body</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={config.allowEditBody === false || readOnly}
                rows={4}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none disabled:bg-neutral-100 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Actions without SSO */}
        <div className="border-t pt-4">
          <p className="text-sm text-neutral-500 mb-3">
            Connect Microsoft account for more features, or use basic email options:
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={connectMicrosoft}
              className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
            >
              <svg className="w-4 h-4" viewBox="0 0 21 21" fill="none">
                <rect width="10" height="10" fill="#f25022" />
                <rect x="11" width="10" height="10" fill="#7fba00" />
                <rect y="11" width="10" height="10" fill="#00a4ef" />
                <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
              </svg>
              Connect Microsoft
            </button>
            <button
              onClick={openInOutlook}
              className="px-3 py-2 border border-neutral-300 text-neutral-700 text-sm rounded-lg hover:bg-neutral-50"
            >
              Open in Outlook
            </button>
            <button
              onClick={openMailto}
              className="px-3 py-2 border border-neutral-300 text-neutral-700 text-sm rounded-lg hover:bg-neutral-50"
            >
              Open in Email App
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return <WidgetLoadingState />;
  }

  // Full email composer with SSO
  return (
    <div className="h-full flex flex-col p-4">
      {/* Status messages */}
      {error && (
        <div className="mb-3 p-2 bg-danger-50 border border-danger-200 text-danger-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 p-2 bg-success-50 border border-success-200 text-success-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Email form */}
      <div className="flex-1 space-y-3 overflow-auto">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">To</label>
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={config.allowEditRecipients === false || readOnly}
            placeholder="email@example.com, another@example.com"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none disabled:bg-neutral-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={config.allowEditSubject === false || readOnly}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none disabled:bg-neutral-100"
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-neutral-700 mb-1">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={config.allowEditBody === false || readOnly}
            rows={6}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none disabled:bg-neutral-100 resize-none"
          />
        </div>
      </div>

      {/* Action buttons */}
      {!readOnly && (
        <div className="flex gap-2 pt-4 border-t mt-4">
          {config.sendMode === 'send' && (
            <button
              onClick={handleSend}
              disabled={isSending}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isSending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
              Send Email
            </button>
          )}
          {config.sendMode === 'draft' && (
            <button
              onClick={handleSend}
              disabled={isSending}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-neutral-600 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
            >
              {isSending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
              )}
              Save as Draft
            </button>
          )}
          <button
            onClick={openInOutlook}
            className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
            title="Open in Outlook desktop app"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default OutlookWidgetRenderer;
